import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, ShieldCheck } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { authErrorMessage } from '@/lib/auth';
import { useTwoFactorEnable, useTwoFactorSetup } from '@/lib/two-factor';
import { BackupCodesView } from './backup-codes-view';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Stage = 'qr' | 'verify' | 'done';

export function TwoFactorSetupDialog({ open, onOpenChange }: Props) {
  const setup = useTwoFactorSetup();
  const enable = useTwoFactorEnable();

  const [stage, setStage] = useState<Stage>('qr');
  const [secret, setSecret] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  // Reset + kick off /setup whenever the dialog opens. `setup` is from
  // react-query — its identity changes every render, so it must be OUTSIDE
  // the dep array (otherwise the mutate→pending→re-render→mutate cycle is
  // an infinite loop). Only the open-edge should drive this effect.
  useEffect(() => {
    if (!open) return;
    setStage('qr');
    setCode('');
    setPassword('');
    setBackupCodes([]);
    setSecret('');
    setQrDataUrl('');
    setup.mutate(undefined, {
      onSuccess: (data) => {
        setSecret(data.secret);
        // Dynamic import keeps the ~50KB qrcode lib out of the main bundle —
        // only fetched the first time a user opens the 2FA setup dialog.
        void import('qrcode')
          .then((mod) =>
            mod.default.toDataURL(data.otpauthUri, {
              errorCorrectionLevel: 'M',
              margin: 1,
              width: 240,
            }),
          )
          .then(setQrDataUrl, () => {
            // QR render failure — user can still type secret manually below.
          });
      },
    });
  }, [open]);

  const handleVerify = () => {
    if (!secret) return;
    enable.mutate(
      { secret, code: code.replace(/\s+/g, ''), password },
      {
        onSuccess: (data) => {
          setBackupCodes(data.backupCodes);
          setStage('done');
        },
      },
    );
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  // Friendlier render of the secret in 4-char chunks for typing into apps that
  // don't support QR (e.g. some hardware tokens).
  const formattedSecret = secret.replace(/(.{4})/g, '$1 ').trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="text-brand-600 h-5 w-5" />
            เปิดใช้งาน 2FA
          </DialogTitle>
          <DialogDescription>
            ใช้แอป Authenticator (Google Authenticator, Authy, 1Password ฯลฯ) เพื่อสแกน QR
            แล้วใส่รหัส 6 หลัก
          </DialogDescription>
        </DialogHeader>

        {stage === 'qr' && (
          <div className="space-y-4">
            {setup.isPending ? (
              <div className="flex h-[240px] items-center justify-center">
                <Loader2 className="text-brand-600 h-8 w-8 animate-spin" />
              </div>
            ) : setup.isError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{authErrorMessage(setup.error)}</AlertDescription>
              </Alert>
            ) : (
              <>
                <div className="flex justify-center">
                  {qrDataUrl ? (
                    <img
                      src={qrDataUrl}
                      alt="QR code สำหรับ scan ลง Authenticator app"
                      width={240}
                      height={240}
                      className="rounded-md border border-slate-200 dark:border-slate-800"
                    />
                  ) : (
                    <div className="flex h-[240px] w-[240px] items-center justify-center rounded-md border border-dashed border-slate-300 text-sm text-slate-500">
                      สแกน QR ไม่ได้ — copy ค่าด้านล่าง
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-slate-600 dark:text-slate-400">
                    หรือพิมพ์ secret ลงในแอป (Base32):
                  </p>
                  <code className="block break-all rounded bg-slate-100 px-3 py-2 font-mono text-xs text-slate-800 dark:bg-slate-800 dark:text-slate-200">
                    {formattedSecret}
                  </code>
                </div>
              </>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                ยกเลิก
              </Button>
              <Button type="button" onClick={() => setStage('verify')} disabled={!secret}>
                ถัดไป
              </Button>
            </DialogFooter>
          </div>
        )}

        {stage === 'verify' && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              ใส่รหัส 6 หลักจากแอป + รหัสผ่านบัญชี เพื่อยืนยันว่า setup ถูกต้อง
            </p>
            {enable.isError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{authErrorMessage(enable.error)}</AlertDescription>
              </Alert>
            )}
            <div>
              <Label htmlFor="totp-code" className="mb-1.5 block">
                รหัส 6 หลัก
              </Label>
              <Input
                id="totp-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="font-mono tracking-widest"
              />
            </div>
            <div>
              <Label htmlFor="password-confirm" className="mb-1.5 block">
                รหัสผ่านบัญชี
              </Label>
              <PasswordInput
                id="password-confirm"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStage('qr')}>
                ย้อนกลับ
              </Button>
              <Button
                type="button"
                onClick={handleVerify}
                disabled={code.length !== 6 || !password || enable.isPending}
              >
                {enable.isPending ? 'กำลังเปิดใช้งาน...' : 'เปิดใช้งาน'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {stage === 'done' && (
          <div className="space-y-4">
            <Alert>
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription>
                เปิดใช้งาน 2FA สำเร็จ ครั้งต่อไปที่ login ต้องใส่รหัสจากแอป
              </AlertDescription>
            </Alert>
            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Backup codes (10 รหัส)
              </p>
              <BackupCodesView codes={backupCodes} />
            </div>
            <DialogFooter>
              <Button type="button" onClick={handleClose}>
                เข้าใจแล้ว
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
